import { Component, OnInit } from '@angular/core';
import { DataserviceService } from '../dataservice.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NzModalService } from 'ng-zorro-antd/modal';
import { AppComponent } from '../app.component';

interface Permission {
  name: string;
  description: string;
  module: string;
  company_id: string;
}

interface Role {
  id: string;
  name: string;
  description: string;
  permissions: Permission[];
}

@Component({
  selector: 'app-role-permissions',
  templateUrl: './role-permissions.component.html',
  styleUrls: ['./role-permissions.component.css']
})
export class RolePermissionsComponent implements OnInit {
  roles: Role[] = [];
  selectedRole: Role | null = null;
  isModalVisible = false;
  isAddRoleModalVisible = false;
  isLoading = false;
  roleForm: FormGroup;
  permissionOptions: { label: string, value: Permission }[] = [];
  permissions: Permission[] = [];
  isAddPermissionModalVisible = false;
  isNewModule = false;
  permissionForm: FormGroup;
  isPermissionsModalVisible = false;
  isEditRoleModalVisible = false;
  isEditPermissionModalVisible = false;
  editingPermission: Permission | null = null;
  editingRole: Role | null = null;
  isSuperAdmin: boolean = false;

  companies: any;
  selectedCompany: any;
  currentUser: any;

  constructor(
    private dataService: DataserviceService,
    private message: NzMessageService,
    private modal: NzModalService,
    private fb: FormBuilder,
    private app: AppComponent
  ) {
    this.roleForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      description: [''],
      permissions: [[]]
    });

    this.permissionForm = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      module: ['', Validators.required],
      newModule: [''],
      description: ['']
    });

    this.permissionForm.get('module')?.valueChanges.subscribe(value => {
      this.isNewModule = value === '_new';
      if (this.isNewModule) {
        this.permissionForm.get('newModule')?.setValidators([Validators.required]);
      } else {
        this.permissionForm.get('newModule')?.clearValidators();
      }
      this.permissionForm.get('newModule')?.updateValueAndValidity();
    });
  }

  ngOnInit() {
    this.currentUser = JSON.parse(localStorage.getItem('user')!);
    this.isSuperAdmin = this.currentUser?.super_admin;
    this.selectedCompany = this.currentUser?.company_id;
    this.loadPermissions();
    // this.loadRoles();

    if (this.hasPermissions('view_all_companies')) {
      this.getCompanies();
    } else if (this.hasPermissions('view_distributor_companies')) {
      this.getDistributorCompanies();
    }

    this.getRolesByCompany();
  }

  getRolesByCompany() {
    this.isLoading = true
    this.dataService.getData(`roles/rolesByCompany/${this.selectedCompany}`)
      .subscribe({
        next: (roles: Role[]) => {
          this.roles = roles.map(role => ({
            ...role,
            permissions: role.permissions || []
          }));
          this.isLoading = false;
        },
        error: (err: any) => {
          console.log(err)
        }
      })
  }

  getCompanies() {
    this.dataService.getData('company')
      .subscribe({
        next: (res: any) => {
          this.companies = res?.data;
        },
        error: (err: any) => {
          console.log('error on get all companies: ', err)
          // this.errorNotification('Failed to get all companies')
        }
      })
  }

  getDistributorCompanies() {
    this.dataService.getData(`company/companies-by-distributor/${this.currentUser?.distributor_id}`)
      .subscribe({
        next: (res: any) => {
          this.companies = res?.data;
        },
        error: (err: any) => {
          console.log('error on get companies by distributor: ', err)
          // this.errorNotification('Failed to get companies by distributor')
        }
      });
  }

  getPermissionsByCompany() {

  }

  hasPermissions(permission: string): boolean {
    return this.app.hasPermission(permission)
  }

  loadPermissions() {
    this.isLoading = true;
    this.dataService.getData('permissions').subscribe({
      next: (permissions: Permission[]) => {
        this.permissions = permissions.filter((p: any) => {
          // Hide chat permissions from non-super admins
          if (p.name === 'view_chat' && !this.currentUser?.super_admin) {
            return false;
          }
          return true;
        });
        this.permissionOptions = this.permissions.map(p => ({ label: p.name, value: p }));
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading permissions:', error);
        this.message.error('Failed to load permissions');
        this.isLoading = false;
      }
    });
  }

  // loadRoles() {
  //   this.isLoading = true;
  //   this.dataService.getData('roles').subscribe({
  //     next: (roles: Role[]) => {
  //       this.roles = roles.map(role => ({
  //         ...role,
  //         permissions: role.permissions || []
  //       }));
  //       this.isLoading = false;
  //     },
  //     error: (error) => {
  //       console.error('Error loading roles:', error);
  //       this.message.error('Failed to load roles');
  //       this.isLoading = false;
  //     }
  //   });
  // }

  getUniqueModules(): string[] {
    return [...new Set(this.permissions.map(p => p.module))];
  }

  getPermissionsByModule(module: string): Permission[] {
    return this.permissions.filter(p => p.module === module);
  }

  showPermissionModal(role: Role) {
    this.selectedRole = { ...role };
    this.isModalVisible = true;
  }

  showAddRoleModal() {
    this.roleForm.reset();
    this.isAddRoleModalVisible = true;
  }

  hasPermission(permission: Permission): boolean {
    return this.selectedRole?.permissions?.some(
      (p: Permission) => p.name === permission.name
    ) ?? false;
  }

  togglePermission(permission: Permission) {
    if (!this.selectedRole) return;

    if (permission.name === 'view_chat' && !this.currentUser?.super_admin) {
      this.message.warning('Only super admins can manage chat permissions');
      return;
    }

    const permissions = [...(this.selectedRole.permissions || [])];
    const index = permissions.findIndex(p => p.name === permission.name);

    if (index === -1) {
      permissions.push(permission);
    } else {
      permissions.splice(index, 1);
    }

    this.selectedRole = {
      ...this.selectedRole,
      permissions
    };
  }

  saveRolePermissions() {
    if (!this.selectedRole) return;

    this.isLoading = true;
    this.dataService.updateData(
      { permissions: this.selectedRole.permissions },
      `roles/${this.selectedRole.id}/permissions`
    ).subscribe({
      next: () => {
        this.message.success('Role permissions updated successfully');
        this.getRolesByCompany();
        this.isModalVisible = false;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error updating permissions:', error);
        this.message.error('Failed to update permissions');
        this.isLoading = false;
      }
    });
  }

  handleAddRole() {
    if (this.roleForm.valid) {
      const newRole = this.roleForm.value;

      newRole['company_id'] = this.selectedCompany

      this.isLoading = true;
      this.dataService.postData(newRole, 'roles').subscribe({
        next: () => {
          this.message.success('Role created successfully');
          this.getRolesByCompany();
          this.isAddRoleModalVisible = false;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error creating role:', error);
          this.message.error('Failed to create role');
          this.isLoading = false;
        }
      });
    } else {
      Object.values(this.roleForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsTouched();
        }
      });
    }
  }

  confirmDeleteRole(roleId: string) {
    this.modal.confirm({
      nzTitle: 'Are you sure you want to delete this role?',
      nzContent: 'This action cannot be undone.',
      nzOkText: 'Yes',
      nzOkType: 'primary',
      nzOkDanger: true,
      nzOnOk: () => this.deleteRole(roleId),
      nzCancelText: 'No',
    });
  }

  deleteRole(roleId: string) {
    this.dataService.deleteData(`roles/${roleId}`).subscribe({
      next: () => {
        this.message.success('Role deleted successfully');
        this.getRolesByCompany();
      },
      error: (error) => {
        console.error('Error deleting role:', error);
        this.message.error('Failed to delete role');
      }
    });
  }

  showAddPermissionModal() {
    this.permissionForm.reset();
    this.isAddPermissionModalVisible = true;
  }

  handleAddPermission() {
    if (this.permissionForm.valid) {
      const formValue = this.permissionForm.value;
      const newPermission: any = {
        name: formValue.name,
        module: formValue.module === '_new' ? formValue.newModule : formValue.module,
        description: formValue.description,
      };

      // If it's a chat permission, mark it as super_admin_only
      if (formValue.name === 'view_chat') {
        if (!this.currentUser?.super_admin) {
          this.message.warning('Only super admins can create chat permissions');
          return;
        }
        newPermission.super_admin_only = true;
      }

      this.isLoading = true;
      this.dataService.postData({ newPermission }, 'permissions').subscribe({
        next: () => {
          this.message.success('Permission created successfully');
          this.loadPermissions();
          this.isAddPermissionModalVisible = false;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error creating permission:', error);
          this.message.error('Failed to create permission');
          this.isLoading = false;
        }
      });
    } else {
      Object.values(this.permissionForm.controls).forEach(control => {
        if (control.invalid) {
          control.markAsTouched();
        }
      });
    }
  }

  showPermissionsModal() {
    this.isPermissionsModalVisible = true;
  }

  confirmDeletePermission(permission: Permission) {
    this.modal.confirm({
      nzTitle: 'Are you sure you want to delete this permission?',
      nzContent: 'This action cannot be undone.',
      nzOkText: 'Yes',
      nzOkType: 'primary',
      nzOkDanger: true,
      nzOnOk: () => this.deletePermission(permission),
      nzCancelText: 'No',
    });
  }

  deletePermission(permission: Permission) {
    if (!this.currentUser.super_admin === true) {
      this.message.warning('Only admins can delete this permission');
      return;
    }

    this.isLoading = true;
    this.dataService.deleteData(`permissions/${permission.name}`).subscribe({
      next: () => {
        this.message.success('Permission deleted successfully');
        this.loadPermissions();
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error deleting permission:', error);
        this.message.error('Failed to delete permission');
        this.isLoading = false;
      }
    });
  }

  showEditRoleModal(role: Role) {
    this.editingRole = { ...role };
    this.roleForm.patchValue({
      name: role.name,
      description: role.description,
      permissions: role.permissions
    });
    this.isEditRoleModalVisible = true;
  }

  handleEditRole() {
    if (this.roleForm.valid && this.editingRole) {
      const updatedRole = {
        name: this.roleForm.value.name,
        description: this.roleForm.value.description
      };

      this.isLoading = true;
      this.dataService.updateData(updatedRole, `roles/${this.editingRole.id}`).subscribe({
        next: () => {
          this.message.success('Role updated successfully');
          this.getRolesByCompany();
          this.isEditRoleModalVisible = false;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error updating role:', error);
          this.message.error('Failed to update role');
          this.isLoading = false;
        }
      });
    }
  }

  showEditPermissionModal(permission: Permission) {
    this.editingPermission = { ...permission };
    this.permissionForm.patchValue({
      name: permission.name,
      module: permission.module,
      description: permission.description
    });
    this.isEditPermissionModalVisible = true;
  }

  handleEditPermission() {
    if (this.permissionForm.valid && this.editingPermission) {
      if (this.editingPermission.name === 'view_chat' && !this.currentUser?.super_admin) {
        this.message.warning('Only super admins can edit chat permissions');
        return;
      }

      const formValue = this.permissionForm.value;
      const updatedPermission: any = {
        name: formValue.name,
        module: formValue.module === '_new' ? formValue.newModule : formValue.module,
        description: formValue.description,
      };

      // Ensure chat permissions remain super_admin_only
      if (formValue.name === 'view_chat') {
        updatedPermission.super_admin_only = true;
      }

      this.isLoading = true;
      this.dataService.updateData(updatedPermission, `permissions/${this.editingPermission.name}`).subscribe({
        next: () => {
          this.message.success('Permission updated successfully');
          this.loadPermissions();
          this.isEditPermissionModalVisible = false;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error updating permission:', error);
          this.message.error('Failed to update permission');
          this.isLoading = false;
        }
      });
    }
  }

  // isSuperAdminOnlyPermission(permission: Permission): boolean {
  //   return permission.name === 'view_chat';
  // }

  // canManagePermission(permission: Permission): boolean {
  //   if (this.isSuperAdminOnlyPermission(permission)) {
  //     return this.currentUser?.super_admin === true;
  //   }
  //   return true;
  // }

  superAdminOnly(permission: any){
    return permission.super_admin_only === true;
  }

  canAddPermissions(): boolean {
    return this.currentUser?.super_admin === true || true;
  }

  isChatPermission(permissionName: string): boolean {
    return permissionName === 'view_chat';
  }

}